/* eslint-disable import/first */
import { notifyAuthStorageChanged } from './auth';
import {
  firebaseOTP,
  type FirebaseOtpInitOptions,
  type FirebaseWebClientConfig,
} from './firebaseOTP';
import { allowDevOtpAssistUI } from './otpEnv';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

type ProviderConfigResponse = {
  provider: string;
  firebaseConfig?: FirebaseWebClientConfig;
  otpRuntime?: {
    environment?: string;
    exposeDevCode?: boolean;
    productionFirebaseIdTokenRequired?: boolean;
  };
  firebaseOtp?: {
    deliveryMode?: 'client' | 'proxy';
    devFallbackEnabled?: boolean;
    backupSmsAvailable?: boolean;
  };
  appCheck?: {
    siteKey?: string;
    debugInDev?: boolean;
  };
};

function isInvalidAppCredential(err: unknown): boolean {
  const code = (err as { code?: string })?.code || '';
  const msg = String((err as Error)?.message || '');
  return (
    code === 'auth/invalid-app-credential' ||
    code === 'auth/invalid-app-credentials' ||
    msg.includes('invalid-app-credential') ||
    msg.includes('app credential')
  );
}

/** Fill appId / messagingSenderId from storefront env when Admin left them blank (local/VPS). */
function mergeFirebaseWebEnv(
  cfg: FirebaseWebClientConfig | null | undefined
): FirebaseWebClientConfig {
  const base =
    cfg && typeof cfg === 'object'
      ? { ...cfg }
      : { apiKey: '', projectId: '' };
  const appId =
    String(base.appId || '').trim() ||
    String(process.env.REACT_APP_FIREBASE_APP_ID || '').trim();
  const messagingSenderId =
    String(base.messagingSenderId || '').trim() ||
    String(process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '').trim();
  return { ...base, appId, messagingSenderId };
}

export const otpService = {
  // Check if user exists with phone
  async checkUser(phone: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/check-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Check user error:', error);
      throw new Error(error.message || 'Failed to check user');
    }
  },

  async fetchProviderConfig(): Promise<ProviderConfigResponse> {
    const res = await fetch(`${API_BASE_URL}/otp/provider-config`);
    return res.json();
  },

  // Initialize Firebase (idempotent; firebaseOTP handles config changes)
  async initFirebase(
    cached?: FirebaseWebClientConfig | null,
    cachedMeta?: Pick<ProviderConfigResponse, 'firebaseOtp' | 'appCheck'> | null
  ) {
    const data: ProviderConfigResponse = cached
      ? {
          provider: 'firebase',
          firebaseConfig: cached,
          firebaseOtp: cachedMeta?.firebaseOtp,
          appCheck: cachedMeta?.appCheck,
        }
      : await this.fetchProviderConfig();

    if (data.provider !== 'firebase') {
      return data;
    }

    const cfg = mergeFirebaseWebEnv(data.firebaseConfig);
    if (!cfg?.apiKey?.trim() || !cfg?.projectId?.trim()) {
      throw new Error(
        'Firebase API Key and Project ID are missing. Save them in Admin → Ultra Settings → OTP (Firebase).'
      );
    }

    const initOptions: FirebaseOtpInitOptions = {
      deliveryMode: data.firebaseOtp?.deliveryMode || 'client',
      appCheckSiteKey: data.appCheck?.siteKey,
      appCheckDebugInDev: data.appCheck?.debugInDev,
    };

    try {
      firebaseOTP.initialize(cfg, initOptions);
    } catch (error: any) {
      console.error('Failed to init Firebase:', error);
      throw new Error(
        error?.message ||
          'Firebase could not start. Check Admin OTP fields and browser console.'
      );
    }
    return data;
  },

  async sendFirebaseOtpWithRecovery(phone: string, config: ProviderConfigResponse) {
    await this.initFirebase(config.firebaseConfig, config);
    firebaseOTP.clearSession();
    await firebaseOTP.teardownRecaptcha('recaptcha-container');
    await firebaseOTP.setupRecaptcha('recaptcha-container');

    const trySend = () => firebaseOTP.sendOTP(phone);

    try {
      return await trySend();
    } catch (firstErr: any) {
      if (
        firebaseOTP.getDeliveryMode() === 'client' &&
        isInvalidAppCredential(firstErr)
      ) {
        console.warn(
          '[OTP] Browser Firebase credential rejected — retrying via API proxy with fresh reCAPTCHA…'
        );
        firebaseOTP.clearSession();
        firebaseOTP.setDeliveryMode('proxy');
        await firebaseOTP.teardownRecaptcha('recaptcha-container');
        await firebaseOTP.setupRecaptcha('recaptcha-container');
        try {
          return await trySend();
        } catch (proxyErr) {
          firstErr = proxyErr;
        }
      }

      if (
        config.firebaseOtp?.devFallbackEnabled !== false &&
        process.env.NODE_ENV !== 'production'
      ) {
        await firebaseOTP.teardownRecaptcha('recaptcha-container');
        const response = await fetch(`${API_BASE_URL}/otp/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || firstErr.message || 'Failed to send OTP');
        }
        const expose = allowDevOtpAssistUI(config.otpRuntime?.exposeDevCode);
        return {
          success: true,
          message: expose
            ? data.message || 'Local dev OTP ready.'
            : data.message ||
              'OTP generated on server for local testing. Check the API terminal logs — SMS was not sent.',
          devOtp: expose ? data.devOtp : undefined,
          devOtpHint: expose ? data.devOtpHint : undefined,
          smsSent: data.smsSent === true,
        };
      }

      throw firstErr;
    }
  },

  // Send OTP to phone
  async sendOTP(phone: string) {
    try {
      const config = await this.fetchProviderConfig();

      if (config.provider === 'firebase') {
        return await this.sendFirebaseOtpWithRecovery(phone, config);
      }

      // Fallback to regular OTP
      const response = await fetch(`${API_BASE_URL}/otp/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      return data;
    } catch (error: any) {
      console.error('Send OTP error:', error);
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }
  },

  // Verify OTP — returns firebaseIdToken when provider is Firebase (required for production login/register).
  async verifyOTP(
    phone: string,
    otp: string
  ): Promise<{ success: boolean; message?: string; firebaseIdToken?: string; user?: unknown }> {
    try {
      // Check if Firebase is being used
      const config = await this.fetchProviderConfig();

      if (config.provider === 'firebase') {
        await this.initFirebase(config.firebaseConfig, config);
        try {
          const result = await firebaseOTP.verifyOTP(otp);
          if (result.success) {
            return {
              success: true,
              user: result.user,
              firebaseIdToken: result.idToken,
            };
          }
          throw new Error(result.error || 'Invalid OTP');
        } catch (firebaseErr: any) {
          const msg = String(firebaseErr?.message || '');
          const devDbFallback =
            config.firebaseOtp?.devFallbackEnabled !== false &&
            process.env.NODE_ENV !== 'production' &&
            msg.includes('No pending OTP verification');
          if (devDbFallback) {
            const response = await fetch(`${API_BASE_URL}/otp/verify-otp`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone, otp }),
            });
            const data = await response.json();
            if (response.ok && data.success) {
              return data;
            }
          }
          throw firebaseErr;
        }
      }
      
      // Fallback to regular OTP verification
      const response = await fetch(`${API_BASE_URL}/otp/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, otp }),
      });

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Verify OTP error:', error);
      throw new Error(error.message || 'Failed to verify OTP');
    }
  },

  // Resend OTP
  async resendOTP(phone: string) {
    try {
      const config = await this.fetchProviderConfig();

      if (config.provider === 'firebase') {
        return await this.sendFirebaseOtpWithRecovery(phone, config);
      }

      const response = await fetch(`${API_BASE_URL}/otp/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend OTP');
      }

      return data;
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      throw new Error(error.message || 'Failed to resend OTP. Please try again.');
    }
  },

  // Login with phone and OTP
  async phoneLogin(phone: string, otp: string, firebaseIdToken?: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/phone-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone,
          otp,
          ...(firebaseIdToken ? { firebaseIdToken } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.token && data.user) {
        const isAdmin = data.user?.role === 'admin';
        if (isAdmin) {
          localStorage.setItem('adminToken', data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.user));
        } else {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        notifyAuthStorageChanged();
      }

      return data;
    } catch (error: any) {
      console.error('Phone login error:', error);
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  },

  // Register with OTP verification
  async registerWithOTP(data: {
    firstName: string;
    lastName: string;
    email?: string;
    phone: string;
    password: string;
    otp: string;
    referralCode?: string;
    firebaseIdToken?: string;
  }) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/register-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Registration failed');
      }

      return result;
    } catch (error: any) {
      console.error('Register with OTP error:', error);
      throw new Error(error.message || 'Registration failed. Please try again.');
    }
  },
};
